"use client"
import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import { useLazyThirdParty } from "@/hooks/useLazyThirdParty"
import { useConsent } from "@/lib/consent"

interface GHLChatWidgetProps {
  nonce?: string
}

// Live chat — only after the visitor accepts "Live chat" in the cookie banner (src/lib/consent.ts), and
// then on their first interaction (useLazyThirdParty: the GHL bundle is ~520 KB). The GHL loader needs a
// div[data-chat-widget] with both ids on it; the widget itself can also be set to load on interaction in GHL.
export function GHLChatWidget({ nonce = '' }: GHLChatWidgetProps) {
  const loaded = useRef(false)
  const interacted = useLazyThirdParty()
  const allowed = useConsent("functional")
  const pathname = usePathname()
  // The client portal (/track/…) is a client-facing status page — no chat there.
  const onPortal = pathname?.startsWith("/track") ?? false

  // If the widget was already injected on another page, hide it while on the
  // portal (client-side navigation keeps injected DOM alive across routes).
  useEffect(() => {
    document.querySelectorAll<HTMLElement>("[data-chat-widget], chat-widget").forEach(el => {
      el.style.display = onPortal ? "none" : ""
    })
  }, [onPortal])

  useEffect(() => {
    if (!interacted || !allowed || onPortal || loaded.current) return
    loaded.current = true

    const div = document.createElement("div")
    div.setAttribute("data-chat-widget", "")
    div.setAttribute("data-widget-id", "68c905cf1c15b470ad4f3a1b")
    div.setAttribute("data-location-id", "2YVSGppZ3t1nNSl74HPu")
    document.body.appendChild(div)

    const script = document.createElement("script")
    script.src = "https://widgets.leadconnectorhq.com/loader.js"
    script.dataset.resourcesUrl = "https://widgets.leadconnectorhq.com/chat-widget/loader.js"
    script.dataset.widgetId = "68c905cf1c15b470ad4f3a1b"
    script.async = true
    if (nonce) script.nonce = nonce
    document.body.appendChild(script)
  }, [nonce, interacted, allowed, onPortal])

  return null
}
